import axios from 'axios';
import dayjs from 'dayjs';
import {
  CHATBOT_PO_ACTION,
  CHATBOT_PO_SAVE,
  CHATBOT_PO_UPLOAD,
} from '../../Constants/Config';
import { formatMoneyForApi } from '../NewPurchaseOrder/createUtils';
import { PO_LINE_QTY_MAX } from '../NewPurchaseOrder/createConstants';
import {
  costPerItemFromExtractedLine,
  DUPLICATE_UPC_MESSAGE,
  findDuplicateUpcRows,
  isStockDueBeforeIssue,
  lineTotalAmount,
  poCreateAfterQtyDisplay,
  PO_STOCK_DUE_BEFORE_ISSUE_MSG,
  poLineProductVariantSelectionKey,
} from './poCreateLogic';
import {
  appendChatbotAuthFields,
  buildChatbotApiUrl,
  getChatbotAuthFields,
  getChatbotRequestHeaders,
} from './chatbotAuthFields';

const PO_REQUEST_TIMEOUT_MS = 120000;

export const getPurchaseOrderPdfError = (error, fallback) => {
  const data = error?.response?.data;
  const detail = data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg || item?.message)
      .filter(Boolean);
    if (messages.length) return messages.join(', ');
  }
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof data?.msg === 'string' && data.msg.trim()) return data.msg;
  if (!error?.response) {
    return 'The request timed out or the connection was lost. Check the purchase order list before trying again.';
  }
  return error?.message || fallback;
};

/** POST /python/chatbot/po/action | /po/save */
const postJson = (path, body) =>
  axios.post(buildChatbotApiUrl(path), body, {
    headers: getChatbotRequestHeaders('application/json'),
    timeout: PO_REQUEST_TIMEOUT_MS,
  });

/** POST /python/chatbot/po/upload */
export const uploadPurchaseOrderPdf = async ({ merchantCode, file, threadId }) => {
  const form = new FormData();
  form.append('merchant_code', merchantCode);
  form.append('file', file, file.name);
  if (threadId) form.append('thread_id', threadId);
  appendChatbotAuthFields(form, merchantCode);

  const response = await axios.post(
    buildChatbotApiUrl(CHATBOT_PO_UPLOAD),
    form,
    {
      // Let the browser set multipart boundary; still send Bearer.
      headers: getChatbotRequestHeaders(null),
      timeout: PO_REQUEST_TIMEOUT_MS,
    },
  );
  return response.data || {};
};

/** POST /python/chatbot/po/action — create | explain | cancel */
export const runPurchaseOrderPdfAction = async ({
  merchantCode,
  threadId,
  fileHash,
  action,
}) => {
  const response = await postJson(CHATBOT_PO_ACTION, {
    merchant_code: merchantCode,
    thread_id: threadId,
    file_hash: fileHash,
    action,
    ...getChatbotAuthFields(merchantCode),
  });
  return response.data || {};
};

/** POST /python/chatbot/po/save */
export const savePurchaseOrderFromPdf = async (body) => {
  const response = await postJson(CHATBOT_PO_SAVE, {
    ...body,
    ...getChatbotAuthFields(body?.merchant_code || body?.merchant_id),
  });
  return response.data || {};
};

const asText = (value) => (value == null ? '' : String(value).trim());

export const normalizeIssueDate = (value) => {
  const raw = asText(value);
  if (!raw) return dayjs().format('YYYY-MM-DD');
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const catalogId = (row) =>
  asText(row?.id ?? row?.vendor_id ?? row?.product_id);

const lineQty = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? String(Math.floor(number)) : '';
};

/** Same rule as AI invoicePrefill.isConfirmedPreviewMatch — only auto + chosen binds. */
const isConfirmedCatalogMatch = (line, chosen) => {
  const status = asText(line?.status).toLowerCase();
  return status === 'auto' && Boolean(catalogId(chosen));
};

export const mapVendorCandidate = (row) => ({
  id: catalogId(row),
  name: asText(row?.name ?? row?.vendor_name),
  email: asText(row?.email),
});

export const mapProductChoice = (row) => {
  const productId = asText(row?.product_id ?? row?.id);
  const variantId = asText(row?.variant_id ?? row?.varient_id) || '0';
  return {
    productId,
    variantId,
    title: asText(
      row?.title ?? row?.name ?? row?.product_name ?? row?.description,
    ),
    upc: asText(row?.upc),
    stock: Number(row?.quantity ?? row?.stock ?? 0) || 0,
  };
};

export const buildReviewFromExtraction = (data, attachment) => {
  const invoice = data?.invoice || {};
  const vendor = data?.vendor || {};
  const chosenVendor = vendor?.chosen || null;
  const lines = Array.isArray(data?.line_items) ? data.line_items : [];

  return {
    fileHash: asText(data?.file_hash) || attachment.fileHash,
    fileName: asText(attachment?.fileName),
    reference: asText(invoice.po_number),
    issueDate: normalizeIssueDate(invoice.po_date),
    stockDate: '',
    invoiceSubtotal: invoice.subtotal ?? null,
    invoiceTotal: invoice.total_amount ?? null,
    pagesProcessed: data?.pages_processed ?? null,
    warnings: Array.isArray(data?.warnings)
      ? data.warnings.map((item) => asText(item)).filter(Boolean)
      : [],
    vendorId: catalogId(chosenVendor),
    vendorName: asText(chosenVendor?.name ?? chosenVendor?.vendor_name),
    vendorEmail: asText(chosenVendor?.email),
    vendorStatus: asText(vendor?.status),
    vendorCandidates: (Array.isArray(vendor?.candidates) ? vendor.candidates : [])
      .map(mapVendorCandidate)
      .filter((item) => item.id),
    lines: lines.map((line, index) => {
      const extracted = line?.extracted || {};
      const chosen = line?.chosen || null;
      const matched = isConfirmedCatalogMatch(line, chosen);
      const product = matched ? mapProductChoice(chosen) : null;
      const invoiceName =
        asText(extracted.description) ||
        asText(extracted.name) ||
        asText(extracted.item) ||
        asText(extracted.item_code);
      return {
        key: `line-${index}-${asText(extracted.upc) || index}`,
        // AI Create PO: keep extract payload so unmatched rows can open
        // "Create new product from invoice" with the same prefill.
        fromInvoice: true,
        extracted: {
          name: invoiceName,
          description: asText(extracted.description),
          upc: asText(extracted.upc),
          quantity: extracted.quantity ?? line?.qty ?? null,
          unit_price: extracted.unit_price ?? line?.unit_price ?? null,
          item_code: asText(extracted.item_code),
          source_id: extracted.source_id ?? null,
        },
        extractedDescription: invoiceName,
        extractedUpc: asText(extracted.upc),
        extractedName: invoiceName,
        extractedUnitPrice: extracted.unit_price ?? null,
        status: matched ? 'matched' : asText(line?.status) || 'unmatched',
        qty: lineQty(line?.qty ?? extracted.quantity),
        cost: costPerItemFromExtractedLine(extracted, line),
        note: '',
        productId: product?.productId || '',
        variantId: product?.variantId || '0',
        title: product?.title || '',
        upc: product?.upc || '',
        stock: product?.stock || 0,
        candidates: (Array.isArray(line?.candidates) ? line.candidates : [])
          .map(mapProductChoice)
          .filter((item) => item.productId),
      };
    }),
  };
};

export const lineTotal = (line) => lineTotalAmount(line?.qty, line?.cost);

const isIsoDate = (value) => {
  const text = asText(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const parsed = dayjs(text);
  return parsed.isValid() && parsed.format('YYYY-MM-DD') === text;
};

/** Create-PO validation — vendor is optional for chatbot PO. */
export const reviewValidationError = (review) => {
  if (!isIsoDate(review.issueDate)) {
    return 'Enter a valid issue date.';
  }
  if (review.stockDate && !isIsoDate(review.stockDate)) {
    return 'Enter a valid stock date or leave it blank.';
  }
  if (isStockDueBeforeIssue(review.stockDate, review.issueDate)) {
    return PO_STOCK_DUE_BEFORE_ISSUE_MSG;
  }
  if (!Array.isArray(review.lines) || review.lines.length === 0) {
    return 'Keep at least one item.';
  }

  const keys = new Map();
  for (let index = 0; index < review.lines.length; index += 1) {
    const line = review.lines[index];
    const qty = Number(line.qty);
    const cost = Number(line.cost);
    if (!line.productId) {
      return `Row ${index + 1}: product is required.`;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      return `Row ${index + 1}: quantity must be a positive number.`;
    }
    if (qty > PO_LINE_QTY_MAX) {
      return `Row ${index + 1}: quantity cannot exceed ${PO_LINE_QTY_MAX.toLocaleString()}.`;
    }
    if (!Number.isFinite(cost) || cost < 0) {
      return `Row ${index + 1}: cost must be zero or more.`;
    }
    const selectionKey = poLineProductVariantSelectionKey(line);
    if (selectionKey) {
      if (keys.has(selectionKey)) {
        return `Row ${index + 1}: duplicate of row ${
          keys.get(selectionKey) + 1
        } — same product/variant.`;
      }
      keys.set(selectionKey, index);
    }
  }

  if (findDuplicateUpcRows(review.lines).size > 0) {
    return DUPLICATE_UPC_MESSAGE;
  }
  return '';
};

export const buildPurchaseOrderSaveBody = ({
  merchantCode,
  threadId,
  review,
  isDraft,
  loginEmployeeId,
}) => {
  const orderItems = {};
  review.lines.forEach((line, index) => {
    const qty = Number(line.qty);
    const cost = Number(line.cost);
    const after = poCreateAfterQtyDisplay(line.stock, qty);
    orderItems[String(index)] = {
      product_id: String(line.productId),
      variant_id: String(line.variantId || '0'),
      required_qty: String(qty),
      after_qty: String(after),
      cost_per_item: formatMoneyForApi(cost),
      total_pricing: formatMoneyForApi(qty * cost),
      upc: String(line.upc || ''),
      note: asText(line.note),
      recieved_status: '0',
      title: asText(line.title) || asText(line.extractedDescription),
    };
  });

  return {
    merchant_code: merchantCode,
    thread_id: threadId,
    confirmed: true,
    ...getChatbotAuthFields(merchantCode),
    purchase_order: {
      vendor_id: review.vendorId ? String(review.vendorId) : '',
      vendor_name: review.vendorName || '',
      vendor_email: review.vendorEmail || '',
      issue_date: review.issueDate,
      stock_date: review.stockDate || '0000-00-00',
      reference: asText(review.reference),
      is_draft: isDraft ? 1 : 0,
      created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      login_employee_id: loginEmployeeId || null,
      order_items: orderItems,
    },
  };
};

export const savedPurchaseOrderNumber = (payload) => {
  const nested = payload?.result || payload?.data || {};
  return (
    asText(payload?.po_number) ||
    asText(payload?.po_id) ||
    asText(nested?.po_number) ||
    asText(nested?.po_id)
  );
};

export const isSavedPurchaseOrder = (payload) => payload?.status === true;
